export interface DialoguePage {
  kicker?: string
  title?: string
  body?: string
  bullets?: string[]
  bulletsLabel?: string
  chip?: string
  action?: { label: string; onClick: () => void }
}

export interface DialogueApi {
  open: (pages: DialoguePage[], options?: { onClose?: () => void }) => void
  /** Completes the current reveal, or moves to the next page once complete. */
  advance: () => void
  close: () => void
  isOpen: () => boolean
  /** Driven from the render loop so the reveal shares the app's clock. */
  update: (dt: number) => void
}

const CHARS_PER_SECOND = 46
/** Time cost of one bullet, expressed in character-equivalents. */
const BULLET_COST = 14
const BLIP_EVERY = 3

export function bindDialogue(handlers: { onBlip?: () => void } = {}): DialogueApi {
  const root = document.querySelector<HTMLElement>('#dialogue')!
  const kicker = document.querySelector<HTMLElement>('#dlg-kicker')!
  const title = document.querySelector<HTMLElement>('#dlg-title')!
  const body = document.querySelector<HTMLElement>('#dlg-body')!
  const bulletsLabel = document.querySelector<HTMLElement>('#dlg-bullets-label')!
  const bullets = document.querySelector<HTMLUListElement>('#dlg-bullets')!
  const chip = document.querySelector<HTMLElement>('#dlg-chip')!
  const caret = document.querySelector<HTMLElement>('#dlg-caret')!
  const advanceBtn = document.querySelector<HTMLButtonElement>('#dlg-advance')
  const counter = document.querySelector<HTMLElement>('#dlg-counter')!
  const actionBtn = document.querySelector<HTMLButtonElement>('#dlg-action')!
  const closeBtn = document.querySelector<HTMLButtonElement>('#dlg-close')!

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

  let pages: DialoguePage[] = []
  let pageIndex = 0
  let progress = 0
  let complete = false
  let blipAt = 0
  let onClose: (() => void) | undefined

  const currentPage = () => pages[pageIndex]

  const totalProgress = (page: DialoguePage) =>
    (page.body?.length ?? 0) + (page.bullets?.length ?? 0) * BULLET_COST

  const paint = () => {
    const page = currentPage()
    if (!page) return
    const shown = Math.floor(progress)
    const text = page.body ?? ''
    body.textContent = text.slice(0, shown)
    body.hidden = text === ''

    const bulletList = page.bullets ?? []
    const revealedBullets = Math.min(
      bulletList.length,
      Math.max(0, Math.floor((progress - text.length) / BULLET_COST)),
    )
    bullets.hidden = revealedBullets === 0
    bulletsLabel.hidden = revealedBullets === 0 || !page.bulletsLabel
    if (bullets.childElementCount !== revealedBullets) {
      bullets.replaceChildren()
      for (const line of bulletList.slice(0, revealedBullets)) {
        const item = document.createElement('li')
        item.textContent = line
        bullets.append(item)
      }
    }

    caret.hidden = !complete
    caret.textContent = ''
    if (advanceBtn) {
      advanceBtn.hidden = !complete
      advanceBtn.setAttribute(
        'aria-label',
        pageIndex + 1 >= pages.length ? 'Close' : 'Continue',
      )
    }
  }

  const finishReveal = () => {
    const page = currentPage()
    if (!page) return
    progress = totalProgress(page)
    complete = true
    paint()
  }

  const bindAction = (page: DialoguePage) => {
    if (!page.action) {
      actionBtn.hidden = true
      actionBtn.onclick = null
      return
    }
    actionBtn.hidden = false
    actionBtn.textContent = page.action.label
    actionBtn.onclick = (event) => {
      event.stopPropagation()
      page.action?.onClick()
    }
  }

  const showPage = () => {
    const page = currentPage()
    if (!page) return
    kicker.textContent = page.kicker ?? ''
    kicker.hidden = !page.kicker
    title.textContent = page.title ?? ''
    title.hidden = !page.title
    chip.textContent = page.chip ?? ''
    chip.hidden = !page.chip
    bulletsLabel.textContent = page.bulletsLabel ?? ''
    counter.textContent = pages.length > 1 ? `${pageIndex + 1} / ${pages.length}` : ''
    bindAction(page)
    bullets.replaceChildren()
    progress = 0
    complete = totalProgress(page) === 0
    blipAt = 0
    if (reducedMotion.matches) finishReveal()
    else paint()
  }

  const close = () => {
    if (root.classList.contains('hidden')) return
    root.classList.add('hidden')
    root.setAttribute('aria-hidden', 'true')
    root.inert = true
    pages = []
    actionBtn.hidden = true
    actionBtn.onclick = null
    const callback = onClose
    onClose = undefined
    callback?.()
  }

  const advance = () => {
    if (root.classList.contains('hidden')) return
    if (!complete) {
      finishReveal()
      return
    }
    if (pageIndex + 1 >= pages.length) {
      close()
      return
    }
    pageIndex++
    showPage()
  }

  const open = (next: DialoguePage[], options?: { onClose?: () => void }) => {
    if (next.length === 0) return
    pages = next
    pageIndex = 0
    onClose = options?.onClose
    root.classList.remove('hidden')
    root.removeAttribute('aria-hidden')
    root.inert = false
    showPage()
  }

  const update = (dt: number) => {
    if (complete || root.classList.contains('hidden')) return
    const page = currentPage()
    if (!page) return
    progress += CHARS_PER_SECOND * dt
    const limit = totalProgress(page)
    if (progress >= limit) {
      progress = limit
      complete = true
    }
    if (progress - blipAt >= BLIP_EVERY) {
      blipAt = progress
      handlers.onBlip?.()
    }
    paint()
  }

  advanceBtn?.addEventListener('click', (event) => {
    event.stopPropagation()
    advance()
  })

  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    if (
      target.closest('#dlg-close') ||
      target.closest('#dlg-action') ||
      target.closest('#dlg-advance')
    )
      return
    advance()
  })
  closeBtn.addEventListener('click', (event) => {
    event.stopPropagation()
    close()
  })

  close()
  root.classList.add('hidden')
  return {
    open,
    advance,
    close,
    isOpen: () => !root.classList.contains('hidden'),
    update,
  }
}
